import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, CheckCircle2, Loader2, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const CAT_STYLE = {
  bug: "border-down/40 bg-down/10 text-down",
  suggestion: "border-accent-blue/40 bg-accent-blue/10 text-accent-blue",
  question: "border-warn/40 bg-warn/10 text-warn",
};

export default function FeedbackInbox({ token }) {
  const [data, setData] = useState(null);
  const [acting, setActing] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/feedback`, auth);
      setData(res.data);
    } catch { setData({ feedback: [], new: 0 }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    setActing(id);
    try {
      await axios.patch(`${API}/api/feedback/${id}/resolve`, {}, auth);
      toast.success("Feedback marqué traité.");
      await load();
    } catch { toast.error("Action impossible."); }
    finally { setActing(null); }
  };

  if (!data) return null;

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/api/feedback/export.csv`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "anatradex_feedback.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible."); }
  };

  return (
    <div className="mt-10" data-testid="feedback-inbox">
      <h2 className="font-heading text-xl font-bold flex items-center gap-2 mb-4">
        <MessageSquare size={18} /> Feedback testeurs
        {data.new > 0 && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-warn/20 border border-warn/40 text-warn" data-testid="feedback-new-count">
            {data.new} nouveau(x)
          </span>
        )}
        {data.feedback?.length > 0 && (
          <button onClick={exportCsv} className="ml-auto btn text-xs px-3 py-1.5" data-testid="feedback-export-csv-btn">
            <Download size={12} /> Export CSV
          </button>
        )}
      </h2>
      {!data.feedback?.length ? (
        <div className="panel p-6 text-white/40 text-sm" data-testid="feedback-empty">Aucun feedback pour l&apos;instant.</div>
      ) : (
        <div className="space-y-3">
          {data.feedback.map((f) => (
            <div key={f.id} className={`panel p-4 ${f.status === "resolved" ? "opacity-50" : ""}`} data-testid={`feedback-item-${f.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wider ${CAT_STYLE[f.category] || "border-white/20 text-white/60"}`}>
                  {f.category}
                </span>
                <span className="text-xs text-white/60">{f.email}</span>
                {f.page && <span className="text-[10px] text-white/35 font-mono">{f.page}</span>}
                <span className="text-[10px] text-white/35 ml-auto">{new Date(f.created_at).toLocaleString("fr-FR")}</span>
              </div>
              <p className="text-sm text-white/80 whitespace-pre-wrap mb-2">{f.message}</p>
              {f.status !== "resolved" ? (
                <button onClick={() => resolve(f.id)} disabled={acting === f.id}
                        className="btn text-xs px-3 py-1.5 border-up/40 text-up hover:bg-up/10 disabled:opacity-40"
                        data-testid={`feedback-resolve-${f.id}`}>
                  {acting === f.id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} Marquer traité
                </button>
              ) : (
                <span className="text-[10px] text-up">✓ Traité</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
